/**
 * Facebook account API wrapper. The access token is only ever sent (on create),
 * never returned — the DTO omits it.
 */
import { api } from '@/utils/api';
import type { CreateFacebookAccountInput, FacebookAccountDTO } from '@shared/types';

/** List the current user's linked Facebook (Business Manager) accounts. */
export async function list(): Promise<FacebookAccountDTO[]> {
  const response = await api.get<FacebookAccountDTO[]>('/facebook-accounts');
  return response.data;
}

/** Link a new Facebook account. The server encrypts the access token at rest. */
export async function create(input: CreateFacebookAccountInput): Promise<FacebookAccountDTO> {
  const response = await api.post<FacebookAccountDTO>('/facebook-accounts', input);
  return response.data;
}

/** Remove a linked Facebook account (cascades to its ad accounts server-side). */
export async function remove(id: string): Promise<void> {
  await api.delete(`/facebook-accounts/${id}`);
}

/**
 * Start the "Connect with Facebook" flow: ask the server for the Facebook
 * Login-for-Business dialog URL (JWT-protected), then the caller redirects the
 * browser there. Facebook sends the user back to the server callback, which
 * stores the granted System User token.
 */
export async function getAuthUrl(): Promise<string> {
  const response = await api.get<{ authUrl: string }>('/auth/facebook/authorize');
  return response.data.authUrl;
}

export default { list, create, remove, getAuthUrl };
